import { gql, useQuery } from '@apollo/client';
import { Anchor, Box, Paragraph } from '@zillow/constellation-10';
import React, { useContext, useMemo } from 'react';
import { Folder, ReadRepliedStatus, RenterProgressStatus } from '../../../../types/GraphqlTypes';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import { MessageListEmptyContainerSkeleton } from '../../skeleton';

const FOLDER_TEXT_OPTIONS = {
  [Folder.Archived]: 'No messages in your archive to show.',
  [Folder.Spam]: 'No messages in your spam to show.',
  [Folder.ShowAll]: 'No messages in your inbox to show.',
  [Folder.Inbox]: 'No messages in your inbox to show.',
  [Folder.Favorite]: 'No starred messages',
};

export default function EmptyContainer() {
  const { renterProgressStatus, folder, readRepliedStatus, isByProperty } = useContext(InboxPageContext);
  const { data: inboxPageVariables } = useQuery(GET_INBOX_PAGE_VARIABLES, { fetchPolicy: 'cache-only' });
  const { inboxPageIsLoading, inboxPageShowEmptyContainer } = inboxPageVariables;
  const hasFilters =
    isByProperty ||
    readRepliedStatus !== ReadRepliedStatus.ShowAll ||
    renterProgressStatus !== RenterProgressStatus.ShowAll ||
    inboxPageShowEmptyContainer;
  const showMyPropertiesLink = !hasFilters && !inboxPageIsLoading && folder === Folder.Inbox;
  const messageListText = useMemo(() => {
    switch (renterProgressStatus) {
      case RenterProgressStatus.Tenants:
        return 'Conversations with current tenants will be shown here.';
      case RenterProgressStatus.Tours:
        return 'Once you have a scheduled tour with a renter, be sure to add a note to help organize your leads.';
      case RenterProgressStatus.Applicants:
        return 'Invite promising renters to apply to your listing.';
      case RenterProgressStatus.Inquiries:
        return 'Inquiries from potential renters will be shown here.';
      default:
        return FOLDER_TEXT_OPTIONS[folder];
    }
  }, [renterProgressStatus, folder]);

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}
    >
      {inboxPageIsLoading ? (
        <MessageListEmptyContainerSkeleton />
      ) : (
        <>
          <Paragraph css={{ color: 'text.subtle', marginBottom: 'default' }}>{messageListText}</Paragraph>

          {showMyPropertiesLink && (
            <Paragraph>
              <Anchor href="/rental-manager/properties">Go to My Properties</Anchor>
            </Paragraph>
          )}
        </>
      )}
    </Box>
  );
}

const GET_INBOX_PAGE_VARIABLES = gql`
  query {
    inboxPageIsLoading @client
    inboxPageShowEmptyContainer @client
  }
`;
